import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * Silent protection layer:
 * - No toasts, no warning overlays, no visible messages.
 * - Disables right-click, common copy/save/print shortcuts, drag, PrintScreen.
 * - Hides the page on print (anti-PDF), disables text selection.
 *
 * NOTE: no blur-on-window-blur — it was removed because it caused false
 * positives (blurred page) for members when a notification or another
 * program took focus.
 *
 * IMPORTANT: this is client-side only. Real scraping protection is enforced
 * server-side in the edge functions (UA blocklist, rate-limiting, X-Robots-Tag).
 *
 * This component must stay router-independent: it is also mounted outside
 * BrowserRouter in the maintenance and password-protection branches of App.tsx.
 * For route-aware behavior, use RouteAwareSecurityProtection instead.
 */
export const SecurityProtection = ({ disabled = false }: { disabled?: boolean } = {}) => {
  useEffect(() => {
    if (disabled) return;

    const handleContextMenu = (e: MouseEvent) => {
      e.preventDefault();
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      const isMac = navigator.platform.toUpperCase().indexOf("MAC") >= 0;
      const mod = e.ctrlKey || (isMac && e.metaKey);
      const k = e.key.toLowerCase();

      // Ctrl/Cmd + P (print) / S (save) / U (view-source)
      if (mod && (k === "p" || k === "s" || k === "u")) {
        e.preventDefault();
        return;
      }

      // Copy a non-empty selection
      if (mod && k === "c") {
        const selection = window.getSelection()?.toString();
        if (selection) {
          e.preventDefault();
        }
        return;
      }

      // PrintScreen — overwrite clipboard silently
      if (e.key === "PrintScreen" || e.keyCode === 44) {
        e.preventDefault();
        navigator.clipboard?.writeText?.("").catch(() => {});
        return;
      }

      // DevTools shortcuts
      if (
        (e.ctrlKey && e.shiftKey && (k === "i" || k === "j" || k === "c")) ||
        (isMac && e.metaKey && e.altKey && (k === "i" || k === "j" || k === "c")) ||
        e.key === "F12"
      ) {
        e.preventDefault();
        return;
      }
    };

    const handleDragStart = (e: DragEvent) => {
      e.preventDefault();
    };

    document.addEventListener("contextmenu", handleContextMenu);
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("dragstart", handleDragStart);

    return () => {
      document.removeEventListener("contextmenu", handleContextMenu);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("dragstart", handleDragStart);
    };
  }, [disabled]);

  if (disabled) return null;

  return (
    <style>{`
      @media print {
        body { display: none !important; }
      }
      body {
        -webkit-touch-callout: none;
        -webkit-user-select: none;
        -khtml-user-select: none;
        -moz-user-select: none;
        -ms-user-select: none;
        user-select: none;
      }
    `}</style>
  );
};

/**
 * Router-aware wrapper: disables ALL client-side protection (shortcuts,
 * copy/print/selection blocking) inside the member area (/espace-membre).
 */
export const RouteAwareSecurityProtection = ({ isAdmin }: { isAdmin: boolean }) => {
  const { pathname } = useLocation();
  return (
    <SecurityProtection disabled={isAdmin || pathname === '/espace-membre' || pathname.startsWith('/espace-membre/')} />
  );
};

export default SecurityProtection;
